import { Model, Attr } from 'spraypaint';
import AuthController from '../services/AuthController';
import ApplicationRecord from './ApplicationRecord';
import UserRoles from './UserRoles';

let _supportsPermissions: boolean | null = null;

let _demoUser: User;

@Model()
export default class User extends ApplicationRecord {
  static jsonapiType = 'users';

  static get supportsPermissions() {
    return _supportsPermissions || false;
  }

  static set supportsPermissions(value: boolean) {
    if (typeof _supportsPermissions === 'boolean') {
      throw new Error('User.supportsPermissions should only be set once!');
    }
    _supportsPermissions = value;
  }

  static get demoUser(): User {
    return _demoUser;
  }

  static set demoUser(user: User) {
    if (process.env.NODE_ENV === 'development') {
      _demoUser = user;
    }
  }

  // --- Attributes
  // --------------

  // required
  // maxLength: 254
  @Attr() email!: string;

  // writeOnly
  @Attr() password?: string;

  // writeOnly
  @Attr() currentPassword?: string;

  @Attr() roles?: string[];

  // --- Paths
  // ---------

  /**
   * /backend/api/v1/users/
   *
   * Method: get
   * Params:
   *   - include: string
   *   - fields: object
   *   - sort: string
   *   - page[number]: integer
   *   - page[size]: integer
   *   - sort: string
   *   - filter[roles.in]: string
   *   - filter[search]: string
   *
   * Method: post
   * Body:
   *   - email: string (format: email) (maxLength: 254) (required)
   *   - password: string
   *   - current_password: string
   *   - roles: "Admins"[]
   *
   */

  /**
   * /backend/api/v1/users/{id}/
   *
   * Method: get
   * Params:
   *   - id: string (required)
   *   - include: string
   *   - fields: object
   *   - sort: string
   *   - sort: string
   *   - filter[roles.in]: string
   *   - filter[search]: string
   *
   * Method: patch
   * Params:
   *   - id: string (required)
   * Body:
   *   - email: string (format: email) (maxLength: 254)
   *   - password: string
   *   - current_password: string
   *   - roles: "Admins"[]
   *
   */

  // --- Getters
  // -----------

  get isAdmin(): boolean {
    const Klass = getUserClass();
    if (!Klass.supportsPermissions) {
      return true;
    }
    if (!this.roles) {
      return false;
    }
    return this.roles.indexOf(UserRoles.instance.adminRole) !== -1;
  }

  get isCurrentUser() {
    const Klass = getUserClass();
    const { auth } = Klass;
    if (auth.user && this.id) {
      return auth.user?.id === this.id;
    }
    return false;
  }

  get displayName() {
    return this.email;
  }

  // --- Methods
  // -----------

  static get auth() {
    return AuthController.default;
  }

  static currentUserCan(permission: string): boolean {
    const { user } = this.auth;
    if (!user) {
      return false;
    }

    const Klass = getUserClass();
    if (!Klass.supportsPermissions) {
      return true;
    }

    switch (permission) {
      case 'admin':
        return user.isAdmin;

      default:
        return false;
    }
  }

  // static async register(email: string, password: string) {
  //   const user = new User({ email, password });
  //   await user.save();
  //   return user;
  // }

  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<boolean> {
    const { id } = this;
    if (!id) {
      return false;
    }
    const Klass = getUserClass();
    const user = new Klass({
      id,
      password: newPassword,
      currentPassword,
    });
    return user.save();
  }
}

type Ext<T extends typeof User> = T;

let Klass: Ext<typeof User> = User;
let frozen = false;

export const setUserClass = (klass: typeof Klass) => {
  if (frozen) {
    throw new Error('Cannot set - User class is frozen');
  }
  Klass = klass;
};

export const getUserClass = () => {
  return Klass;
};

export const freezeUserClass = () => {
  frozen = true;
};
